var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useLayoutEffect, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createUseStyles } from 'react-jss';
import { ThemeProvider, useTheme } from '../../theme';
import { useRenderTransition, usePortalContainer, useMemoClasses } from '../../utils/hooks';
var useStyles = createUseStyles({
    base: {
        position: 'absolute',
        transition: function (_a) {
            var transitionDuration = _a.transitionDuration;
            return "transform ".concat(transitionDuration, "ms, opacity ").concat(transitionDuration, "ms");
        },
        left: function (_a) {
            var pos = _a.pos;
            return "".concat(pos.left, "px");
        },
        top: function (_a) {
            var pos = _a.pos;
            return "".concat(pos.top, "px");
        },
        width: 'fit-content',
        transformOrigin: 'top',
        transform: 'scaleY(0.5) translateY(-10px)',
        opacity: 0,
    },
    baseOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Dropdown.base));
    },
    open: {
        transform: 'scaleY(1) translateY(0)',
        opacity: 1,
    },
    openOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Dropdown.open));
    },
}, { name: 'RUIDropdown' });
var DropdownWindow = function (_a) {
    var anchorRect = _a.anchorRect, open = _a.open, windowRef = _a.windowRef, windowNode = _a.windowNode, mode = _a.mode, index = _a.index, onIndexChange = _a.onIndexChange, justify = _a.justify, transitionDuration = _a.transitionDuration, className = _a.className, style = _a.style, position = _a.position;
    var _b = useState({ top: 0, left: 0 }), pos = _b[0], setPos = _b[1];
    var theme = useTheme();
    var styles = useStyles({ theme: theme, pos: pos, transitionDuration: transitionDuration });
    var _c = useRenderTransition(open, transitionDuration), shouldRender = _c.shouldRender, shouldApplyTransition = _c.shouldApplyTransition;
    // reset position when dropdown closes
    useEffect(function () {
        if (!shouldRender)
            setPos({ top: 0, left: 0 });
    }, [shouldRender]);
    // update position of dropdown window based on anchor after it is rendered
    useLayoutEffect(function () {
        if (shouldRender && anchorRect && windowRef.current) {
            var windowRect = windowRef.current.getBoundingClientRect();
            // set initial position based on anchor
            var _a = [anchorRect.bottom + window.scrollY, anchorRect.left + window.scrollX], top_1 = _a[0], left = _a[1];
            if (position != 'bottom') {
                top_1 -= anchorRect.height;
                left += position === 'right' ? anchorRect.width : -windowRect.width;
            }
            // adjust X position based on justification if anchor is bottom
            if (position === 'bottom') {
                if (justify === 'right')
                    left -= windowRect.width - anchorRect.width;
                if (justify === 'center')
                    left -= (windowRect.width - anchorRect.width) / 2;
            }
            // correct Y overflow if it spills on the bottom (dropdown initially has half the height for animation purposes)
            var yOverflow = top_1 + windowRect.height * 2 - window.innerHeight - window.scrollY;
            top_1 -= yOverflow > 0 ? yOverflow : 0;
            // correct X overflow to the right and left
            var rightOverflow = left + windowRect.width - window.innerWidth - window.scrollX;
            left -= rightOverflow > 1 ? rightOverflow : 0;
            left = left < 0 ? 0 : left;
            setPos({ top: top_1, left: left });
        }
    }, [shouldRender, anchorRect]);
    // Focus items when dropdown render or index changes
    useLayoutEffect(function () {
        var _a;
        if (mode.includes('keyboard')) {
            // position has to be set, otherwise the focus on a out of screen window will cause a scroll
            if (open && !!windowRef.current && (pos.top || pos.left)) {
                var list = (_a = windowRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild;
                var item = list === null || list === void 0 ? void 0 : list.children[index];
                item === null || item === void 0 ? void 0 : item.focus();
            }
        }
    }, __spreadArray([open, windowRef, index, pos], mode, true));
    var baseClasses = useMemoClasses([
        styles.base,
        theme.overrides.Dropdown.base && styles.baseOverrides,
        shouldApplyTransition && styles.open,
        theme.overrides.Dropdown.open && shouldApplyTransition && styles.openOverrides,
        'RUIDropdown-base',
        className,
    ], [shouldApplyTransition, theme]);
    // handle navigation key events to move between focused items
    var handleKey = function (event) {
        var _a, _b;
        if (mode.includes('keyboard')) {
            var itemsCount = (_b = (_a = windowRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild) === null || _b === void 0 ? void 0 : _b.children.length;
            if (itemsCount) {
                if (event.key === 'ArrowUp' && index !== 0)
                    onIndexChange(index - 1);
                else if (event.key === 'ArrowDown' && index + 1 < itemsCount)
                    onIndexChange(index + 1);
                else if (event.key === 'PageUp')
                    onIndexChange(0);
                else if (event.key === 'PageDown')
                    onIndexChange(itemsCount - 1);
            }
            // also prevent propagation if it key was any of the navigation to stop scrolling
            if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'].includes(event.key))
                event.preventDefault();
        }
    };
    // needs an extra ThemeProvider because it renders outside root tree on a portal
    var DropdownTree = (React.createElement(ThemeProvider, { theme: theme }, shouldRender ? (React.createElement("div", { ref: windowRef, className: baseClasses, onKeyDown: handleKey, style: style }, windowNode)) : null));
    var container = usePortalContainer('Dropdown');
    return createPortal(DropdownTree, container);
};
export { DropdownWindow as default, DropdownWindow };
